// A deadline the goal text names itself ("by March", "in 12 weeks", "by June
// 2027"), so the deadline question can start on it. Pure TS, web + native.

const DAY = 86_400_000

export type GoalDeadline = {
  dueDate: number
  // The words that named it, as written; dropped from the goal when the user
  // picks a different deadline so title and due date can't disagree.
  phrase: string
  // 'month': "by March" (due at the month's end); 'date': a specific day.
  kind: 'month' | 'date'
}

const EN_MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
]

const NUMBER_WORDS: Record<string, number> = {
  a: 1,
  an: 1,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
}

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

function monthNames(locale?: string): Array<{ name: string; month: number }> {
  const out = EN_MONTHS.flatMap((name, month) => [
    { name, month },
    { name: name.slice(0, 3), month },
    ...(name.length > 4 ? [{ name: `${name.slice(0, 4)}.`, month }] : []),
  ])
  if (locale && !locale.startsWith('en')) {
    try {
      const fmt = new Intl.DateTimeFormat(locale, { month: 'long' })
      for (let m = 0; m < 12; m++)
        out.push({ name: fmt.format(new Date(2026, m, 15)).toLowerCase(), month: m })
    } catch {}
  }
  // Longest first so "june" wins over "jun".
  return out.sort((a, b) => b.name.length - a.name.length)
}

const endOfDay = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).getTime()

const PREP =
  "(?:by|before|until|till|til|in|for|this|next|around|ahead of|para|antes de|hasta|en|avant|d[’']ici|jusqu[’']en|até|em|bis)"

export function deadlineFromGoal(
  text: string,
  now = Date.now(),
  locale?: string,
): GoalDeadline | null {
  const today = new Date(now)
  const found = byMonth(text, today, locale) ?? relative(text, today) ?? yearEnd(text, today)
  if (!found) return null
  // Already past, or too far out to plan toward.
  if (found.dueDate <= now || found.dueDate > now + 5 * 366 * DAY) return null
  return found
}

function byMonth(text: string, today: Date, locale?: string): GoalDeadline | null {
  const names = monthNames(locale)
  const month = `(${names.map((n) => escape(n.name)).join('|')})`
  // "by March", "by the end of March", "by March 15", "by 15 March", "by June 2027"; a
  // bare month needs a preposition ("may" is a verb), a month with a year doesn't.
  const re = new RegExp(
    `(?<![\\p{L}\\d])(?:(${PREP})\\s+(?:the\\s+)?(?:(?:end|start|beginning|middle)\\s+of\\s+|early\\s+|mid[- ]|late\\s+)?)?` +
      `(?:(\\d{1,2})(?:st|nd|rd|th)?\\s+(?:of\\s+)?)?${month}` +
      `(?:\\s+(\\d{1,2})(?:st|nd|rd|th)?(?!\\d))?(?:,?\\s+(\\d{4}))?(?![\\p{L}\\d])`,
    'giu',
  )
  for (const m of text.matchAll(re)) {
    const [phrase, prep, dayBefore, name, dayAfter, yearText] = m
    if (!prep && !yearText) continue
    const monthIdx = names.find((n) => n.name === name.toLowerCase())!.month
    const day = Number(dayBefore ?? dayAfter) || undefined
    let year = yearText ? Number(yearText) : today.getFullYear()
    if (!yearText && monthIdx < today.getMonth()) year++
    if (day && day > new Date(year, monthIdx + 1, 0).getDate()) continue
    const due = new Date(year, monthIdx, day ?? new Date(year, monthIdx + 1, 0).getDate())
    if (!yearText && endOfDay(due) <= today.getTime()) due.setFullYear(year + 1)
    return { dueDate: endOfDay(due), phrase: phrase.trim(), kind: day ? 'date' : 'month' }
  }
  return null
}

function relative(text: string, today: Date): GoalDeadline | null {
  const n = `(\\d{1,3}|${Object.keys(NUMBER_WORDS).join('|')})`
  const re = new RegExp(
    `(?<![\\p{L}\\d])(?:(?:in|within|over|under)\\s+(?:the\\s+next\\s+)?${n}\\s+(day|week|month|year)s?|(\\d{1,3})-(day|week|month|year)(?![\\p{L}]))(?![\\p{L}\\d])`,
    'iu',
  )
  const m = text.match(re)
  if (!m) return null
  const count = m[1] ?? m[3]
  const unit = (m[2] ?? m[4]).toLowerCase()
  const amount = /^\d+$/.test(count) ? Number(count) : NUMBER_WORDS[count.toLowerCase()]
  if (!amount) return null
  const due = new Date(today)
  if (unit === 'day') due.setDate(due.getDate() + amount)
  else if (unit === 'week') due.setDate(due.getDate() + amount * 7)
  else if (unit === 'month') due.setMonth(due.getMonth() + amount)
  else due.setFullYear(due.getFullYear() + amount)
  return { dueDate: endOfDay(due), phrase: m[0].trim(), kind: 'date' }
}

function yearEnd(text: string, today: Date): GoalDeadline | null {
  const m = text.match(
    /(?<![\p{L}\d])(?:by\s+(?:the\s+)?end\s+of\s+(?:the|this)\s+year|by\s+(?:year[- ]end|the\s+end\s+of\s+the\s+year)|this\s+year|by\s+christmas)(?![\p{L}\d])/iu,
  )
  if (!m) return null
  const christmas = /christmas/i.test(m[0])
  const due = new Date(today.getFullYear(), 11, christmas ? 25 : 31)
  return { dueDate: endOfDay(due), phrase: m[0].trim(), kind: 'date' }
}

// The goal without its deadline words: "Run a half marathon by March" → "Run a half marathon".
export function withoutDeadline(text: string, phrase: string): string {
  const i = text.toLowerCase().indexOf(phrase.toLowerCase())
  if (i < 0) return text
  const out = `${text.slice(0, i)} ${text.slice(i + phrase.length)}`
    .replace(/\s+([,.!?;:])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .replace(/(?:[\s,;:–—-]|\b(?:and|by|in))+$/i, '')
    .replace(/^[\s,;:–—-]+/, '')
    .trim()
  return out.length >= 3 ? out : text
}
